// Asignacion de vehiculo y conductor a un viaje (PP-34), eligiendo de la
// nomina de la empresa en esta planta con la elegibilidad a la vista.
//
// Un movil no elegible se puede asignar igual (ADR-014 §2, PLAN §1 decision
// 5): la pantalla lo advierte antes de enviar y la API responde 200 con la
// alerta para la planta. Nada de eso se bloquea en el cliente.
//
// Solo se ofrecen las entradas en la nomina (las bajas no se asignan). Las
// bloqueadas por la planta aparecen como no elegibles: asignarlas es decision
// del proveedor y la planta recibe la alerta.
//
// PP-65: el "Código de móvil" es el numero interno con el que el sistema
// propio de la empresa nombra el movil. Escribirlo selecciona el vehiculo de
// la nomina y propone su ultimo conductor (logic/quick-assign.ts); un codigo
// ajeno o inexistente se explica y no selecciona nada.
import { useId, useState } from "react";
import type { Driver, ProviderTrip, SiteVehicle } from "@ucorp/contracts";
import { Alert } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import { eligibilitySummary } from "../logic/documents.js";
import { sortRoster, VEHICLE_TYPE_LABEL } from "../logic/fleet.js";
import { findByRosterCode, proposeDriver, type CodeLookup } from "../logic/quick-assign.js";
import { EligibilityBadge, Field, SelectField } from "../fleet/parts.js";
import type { RosterState } from "../fleet/useRoster.js";

interface Props {
  trip: ProviderTrip;
  vehicles: RosterState<SiteVehicle>;
  drivers: RosterState<Driver>;
  busy: boolean;
  /** Empresa de la sesion en esta planta: un codigo de otra no se acepta. */
  providerId: string;
  /** Ultimo conductor asignado a cada vehiculo (PP-65). Sin el, no se propone. */
  lastDrivers?: ReadonlyMap<string, string>;
  onSubmit: (vehicleId: string, driverId: string) => void;
  onCancel: () => void;
}

function vehicleOption(v: SiteVehicle): string {
  return `${v.code} · ${v.plate} · ${VEHICLE_TYPE_LABEL[v.type]}${v.eligibility.eligible ? "" : " · No elegible"}`;
}

function driverOption(d: Driver): string {
  return `${d.code} · ${d.name}${d.eligibility.eligible ? "" : " · No elegible"}`;
}

const NO_LAST_DRIVERS: ReadonlyMap<string, string> = new Map();

function codeProblem(lookup: CodeLookup<SiteVehicle>, typed: string): string | undefined {
  switch (lookup.kind) {
    case "unknown":
      return `No hay un vehículo con el código "${typed.trim()}" en la nómina de la empresa en esta planta. Revisar el número o elegirlo de la lista.`;
    case "removed":
      return `El vehículo ${lookup.entry.code} está dado de baja de la nómina y no se puede asignar. Reincorporarlo desde Nómina o elegir otro.`;
    case "ambiguous":
      return `El código coincide con ${lookup.codes.join(" y ")}. Escribirlo tal como figura en la nómina.`;
    default:
      return undefined;
  }
}

function RosterNotice({ state, what }: { state: RosterState<unknown>; what: string }): React.JSX.Element | null {
  if (state.estado === "cargando") return <Alert variant="info">Consultando {what} de la nómina...</Alert>;
  if (state.estado === "denegado") return <Alert variant="error">La cuenta no tiene acceso a la nómina de esta planta.</Alert>;
  if (state.estado === "error") return <Alert variant="error">No se pudo consultar {what} de la nómina. Reintentar en unos minutos.</Alert>;
  if (state.items.length === 0) {
    return <Alert variant="aviso">No hay {what} en la nómina de esta planta. Cargarlos desde Ver nómina.</Alert>;
  }
  return null;
}

export function AssignForm({ trip, vehicles, drivers, busy, providerId, lastDrivers = NO_LAST_DRIVERS, onSubmit, onCancel }: Props): React.JSX.Element {
  const id = useId();
  const reassign = trip.assignmentStatus === "assigned";
  const [vehicleId, setVehicleId] = useState(trip.vehicleId ?? "");
  const [driverId, setDriverId] = useState(trip.driverId ?? "");
  const [code, setCode] = useState("");
  // El aviso de codigo se muestra al salir del campo o con Enter, no mientras se escribe.
  const [codeChecked, setCodeChecked] = useState(false);
  // Conductor que puso la propuesta. Una vez que la persona elige conductor a
  // mano, la propuesta no lo pisa.
  const [proposedId, setProposedId] = useState<string | undefined>(undefined);
  const [driverTouched, setDriverTouched] = useState(false);

  // Solo la nomina de la empresa de la sesion, aunque el puerto ya filtre (PP-65).
  const vehicleRows = vehicles.estado === "listo" ? sortRoster(vehicles.items.filter((v) => v.inRoster && v.providerId === providerId)) : [];
  const driverRows = drivers.estado === "listo" ? sortRoster(drivers.items.filter((d) => d.inRoster && d.providerId === providerId)) : [];
  const vehicle = vehicleRows.find((v) => v.id === vehicleId);
  const driver = driverRows.find((d) => d.id === driverId);
  const unchanged = reassign && vehicleId === trip.vehicleId && driverId === trip.driverId;
  const ineligible = (vehicle !== undefined && !vehicle.eligibility.eligible) || (driver !== undefined && !driver.eligibility.eligible);
  const ready = vehicle !== undefined && driver !== undefined && !unchanged && !busy;
  const lookup: CodeLookup<SiteVehicle> =
    vehicles.estado === "listo" ? findByRosterCode(vehicles.items, providerId, code) : { kind: "empty" };
  const problem = codeChecked ? codeProblem(lookup, code) : undefined;
  const proposed = proposedId !== undefined && driverId === proposedId && vehicle !== undefined;

  const selectVehicle = (nextId: string): void => {
    setVehicleId(nextId);
    if (driverTouched) return;
    const candidate = nextId === "" ? undefined : proposeDriver(nextId, lastDrivers, driverRows, providerId);
    // Sin propuesta, una propuesta anterior vuelve al conductor con el que se abrio el formulario.
    if (candidate !== undefined) setDriverId(candidate.id);
    else if (proposedId !== undefined && driverId === proposedId) setDriverId(trip.driverId ?? "");
    setProposedId(candidate?.id);
  };

  const typeCode = (typed: string): void => {
    setCode(typed);
    setCodeChecked(false);
    if (vehicles.estado !== "listo") return;
    const result = findByRosterCode(vehicles.items, providerId, typed);
    if (result.kind === "found") selectVehicle(result.entry.id);
    // Un codigo que no es de la nomina no deja elegido el vehiculo anterior.
    else if (result.kind !== "empty") selectVehicle("");
  };

  const warnings: string[] = [];
  if (vehicle !== undefined && !vehicle.eligibility.eligible) {
    warnings.push(`Vehículo ${vehicle.code}: ${eligibilitySummary(vehicle.eligibility).detail}.`);
  }
  if (driver !== undefined && !driver.eligibility.eligible) {
    warnings.push(`Conductor ${driver.code}: ${eligibilitySummary(driver.eligibility).detail}.`);
  }

  return (
    <form
      aria-labelledby={`${id}-title`}
      className="flex flex-col gap-3 rounded-md border border-linea bg-pagina p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && vehicle !== undefined && driver !== undefined) onSubmit(vehicle.id, driver.id);
      }}
    >
      <h3 id={`${id}-title`} className="m-0 text-sm font-semibold text-texto">
        {reassign ? "Cambiar vehículo y conductor" : "Asignar vehículo y conductor"}
      </h3>

      <div className="flex flex-col gap-2 sm:max-w-xs">
        <Field
          label="Código de móvil"
          hint="El número interno del vehículo en la empresa: lo elige y propone su último conductor."
          value={code}
          autoComplete="off"
          disabled={vehicles.estado !== "listo" || busy}
          onChange={(event) => typeCode(event.target.value)}
          onBlur={() => setCodeChecked(true)}
          onKeyDown={(event) => {
            // Enter confirma el codigo; asignar sigue siendo el boton.
            if (event.key === "Enter") {
              event.preventDefault();
              setCodeChecked(true);
            }
          }}
        />
        {problem !== undefined && <Alert variant="error">{problem}</Alert>}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <RosterNotice state={vehicles} what="los vehículos" />
          <SelectField
            label="Vehículo"
            value={vehicleId}
            disabled={vehicleRows.length === 0 || busy}
            onChange={(event) => {
              const next = event.target.value;
              selectVehicle(next);
              setCode(vehicleRows.find((v) => v.id === next)?.code ?? "");
              setCodeChecked(false);
            }}
          >
            <option value="">Elegir vehículo</option>
            {vehicleRows.map((v) => (
              <option key={v.id} value={v.id}>
                {vehicleOption(v)}
              </option>
            ))}
          </SelectField>
          {vehicle !== undefined && (
            <div>
              <EligibilityBadge eligibility={vehicle.eligibility} />
            </div>
          )}
          {vehicle !== undefined && vehicle.type !== trip.vehicleType && (
            <p className="m-0 text-xs text-ambar">
              El viaje es de {VEHICLE_TYPE_LABEL[trip.vehicleType].toLowerCase()} y el vehículo es {VEHICLE_TYPE_LABEL[vehicle.type].toLowerCase()}.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <RosterNotice state={drivers} what="los conductores" />
          <SelectField
            label="Conductor"
            value={driverId}
            disabled={driverRows.length === 0 || busy}
            onChange={(event) => {
              setDriverId(event.target.value);
              setProposedId(undefined);
              setDriverTouched(true);
            }}
          >
            <option value="">Elegir conductor</option>
            {driverRows.map((d) => (
              <option key={d.id} value={d.id}>
                {driverOption(d)}
              </option>
            ))}
          </SelectField>
          {driver !== undefined && (
            <div>
              <EligibilityBadge eligibility={driver.eligibility} />
            </div>
          )}
          {proposed && (
            <p className="m-0 text-xs text-texto-suave">
              Propuesto: el último conductor asignado al {vehicle.code} en los viajes de la bandeja. Se puede cambiar.
            </p>
          )}
        </div>
      </div>

      {ineligible && (
        <Alert variant="aviso">
          <span className="block font-medium">Hay un móvil no elegible en esta planta. Se puede asignar igual y la planta recibe una alerta.</span>
          {warnings.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={!ready}>
          {ineligible ? "Asignar igual" : reassign ? "Guardar cambio" : "Asignar"}
        </Button>
        <Button variant="secundario" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
